import { useEffect, useState } from "react";
import {
  planilhaService,
  PlanilhaHistoricoItem,
} from "../services/planilhaService";
import { FiClock, FiUser, FiChevronDown, FiChevronUp } from "react-icons/fi";

interface Props {
  planilhaId: string;
  onClose: () => void;
}

const tipoLabels: Record<string, string> = {
  criacao: "Criação",
  atualizacao_planilha: "Atualização",
  status_planilha: "Status",
  exclusao_planilha: "Exclusão",
  desbloqueio_forcado_admin: "Desbloqueio pelo Admin",
  edicao_cotacao_valores: "Cotação editada (valores)",
  edicao_cotacao_pdf: "Cotação editada (novo PDF)",
  pdf_cotacao_fornecedor: "PDF do fornecedor",
};

function DiffView({
  antes,
  depois,
}: {
  antes?: object | null;
  depois?: object | null;
}) {
  const [expanded, setExpanded] = useState(false);

  const hasData = antes || depois;
  if (!hasData) return null;

  return (
    <div className="mt-2">
      <button
        className="text-xs text-primary flex items-center gap-1"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <FiChevronUp className="h-3 w-3" />
        ) : (
          <FiChevronDown className="h-3 w-3" />
        )}
        {expanded ? "Ocultar detalhes" : "Ver detalhes"}
      </button>
      {expanded && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          {antes && (
            <div>
              <div className="font-semibold text-error mb-1">Antes</div>
              <pre className="bg-base-200 p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap wrap-break-word">
                {JSON.stringify(antes, null, 2)}
              </pre>
            </div>
          )}
          {depois && (
            <div>
              <div className="font-semibold text-success mb-1">Depois</div>
              <pre className="bg-base-200 p-2 rounded overflow-auto max-h-48 whitespace-pre-wrap wrap-break-word">
                {JSON.stringify(depois, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PlanilhaHistoricoModal({ planilhaId, onClose }: Props) {
  const [items, setItems] = useState<PlanilhaHistoricoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setLoading(true);
    planilhaService
      .getHistorico(planilhaId, page, 20)
      .then((res) => {
        setItems(res.items ?? []);
        setTotalPages(res.pagination?.totalPages ?? 1);
      })
      .catch(() => {
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [planilhaId, page]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-base-100 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-base-300">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FiClock className="h-5 w-5 text-primary" />
            Histórico de Alterações
          </h2>
          <button className="btn btn-ghost btn-sm btn-circle" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <span className="loading loading-spinner loading-lg text-primary" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-center text-base-content/60 py-12">
              Nenhum histórico encontrado.
            </p>
          ) : (
            <ol className="relative border-l border-base-300 ml-3 space-y-6">
              {items.map((item) => (
                <li key={item.id} className="ml-6">
                  <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-white ring ring-primary/30">
                    <FiClock className="h-3 w-3 text-primary" />
                  </span>
                  <div className="bg-base-200 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="badge badge-outline text-xs">
                        {tipoLabels[item.tipoAlteracao] ?? item.tipoAlteracao}
                      </span>
                      <span className="text-xs text-base-content/50">
                        {new Date(item.createdAt).toLocaleString("pt-BR")}
                      </span>
                    </div>
                    {item.usuario && (
                      <p className="text-xs text-base-content/70 mt-1 flex items-center gap-1">
                        <FiUser className="h-3 w-3" />
                        {item.usuario.nome || item.usuario.email}
                      </p>
                    )}
                    {item.observacoes && (
                      <p className="text-xs mt-1 text-base-content/80">
                        {item.observacoes}
                      </p>
                    )}
                    <DiffView
                      antes={
                        item.dadosAnteriores
                          ? JSON.parse(item.dadosAnteriores)
                          : null
                      }
                      depois={
                        item.dadosNovos ? JSON.parse(item.dadosNovos) : null
                      }
                    />
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 px-6 py-3 border-t border-base-300">
            <button
              className="btn btn-sm btn-ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </button>
            <span className="text-sm">
              {page} / {totalPages}
            </span>
            <button
              className="btn btn-sm btn-ghost"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
